import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useLocation, useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as ItemComponentActions from "../../../actions/itemComponents";
import * as ItemComponentTypeActions from "../../../actions/itemComponentType";
import * as NavigationActions from "../../../actions/navigation";
import * as ItemComponentPageActions from "../../../actions/pages/itemComponentsPage";
import { Modal, NotificationAlert } from "../../../components/common/index";
import { TextAreaFormInput } from "../../../components/form";
import {
    CheckboxFormInput,
    CustomFormDesigner,
    CustomFormInput,
    PrimitiveFormDesigner,
    PrimitiveFormInput,
    SelectFormInput,
    TextFormInput,
    ValidationMessage,
} from "../../../components/form/index";
import { SOURCE_DOCUMENT_UPLOAD_VIEW } from "../../../components/form/inputs/FileUploadFormInput/FileUploadViews";
import { FileUploadFormInput } from "../../../components/form/inputs/FileUploadFormInput/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import TrialGroupLookupsLayout from "../../../containers/TrialInstitutionLookups/TrialGroupLookupsLayout/TrialGroupLookupsLayout";
import TrialInstitutionLookupsLayout from "../../../containers/TrialInstitutionLookups/TrialInstitutionLookupsLayout/TrialInstitutionLookupsLayout";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { GridRemove } from "../../../enumerations/GridRemove";
import { ModalSize } from "../../../enumerations/ModalSize";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    FormComponentBreadcrumbs,
    FormComponentCreateBreadcrumbs,
    GroupItemComponentBreadcrumbs,
    GroupItemComponentCreateBreadcrumbs,
    InstitutionItemComponentBreadcrumbs,
    InstitutionItemComponentCreateBreadcrumbs,
    ProjectItemComponentBreadcrumbs,
    ProjectItemComponentCreateBreadcrumbs,
    TrialGroupItemComponentBreadcrumbs,
    TrialGroupItemComponentCreateBreadcrumbs,
    TrialInstitutionItemComponentBreadcrumbs,
    TrialInstitutionItemComponentCreateBreadcrumbs,
    TrialItemComponentBreadcrumbs,
    TrialItemComponentCreateBreadcrumbs,
} from "../../../helpers/breadcrumbHelper";
import { SOURCE_DOCUMENT_FILE_CONFIGURATION } from "../../../helpers/file";
import {
    getFormPropertyLabel,
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { setModalTitle } from "../../../helpers/modalHelper";
import {
    SettingsGroupsSubMenu,
    SettingsInstitutionsSubMenu,
    SettingsProjectsSubMenu,
    SettingsQASubMenu,
    SettingsTrialGroupSubMenu,
    SettingsTrialInstitutionSubMenu,
    SettingsTrialSubMenu,
} from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import {
    initialItemComponentPrimitiveState,
    initialItemComponentRuleState,
} from "../../../reducers/reactReduxForms/itemComponent";
import { initialSourceDocumentState } from "../../../reducers/reactReduxForms/sourceDocument";
import {
    requiredSourceDocument,
    requiredText,
} from "../../../validators/index";
import {
    GroupSettingsLayout,
    InstitutionLookupsLayout,
    ProjectLookupsLayout,
    QALookupsLayout,
    TrialLookupsLayout,
} from "../../index";

import "./ItemComponentPage.scss";

interface ItemComponentPageProps {
    form: Dtos.ItemComponent & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    previewModalOpen: boolean;
    addPrimitiveModalOpen: boolean;
    rulesModalOpen: boolean;
    itemComponent: Dtos.ItemComponent;
    lookups: Dtos.Lookup[];
    loadingItemComponent: boolean;
    loadItemComponentSuccess: boolean;
    loadItemComponentFailure: boolean;
    loadItemComponentErrorCode: string;
    savingItemComponent: boolean;
    saveItemComponentSuccess: boolean;
    saveItemComponentFailure: boolean;
    creatingItemComponent: boolean;
    createItemComponentSuccess: boolean;
    createItemComponentFailure: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    itemComponentTypes: Dtos.ItemComponentType[];
    loadingItemComponentTypes: boolean;
    loadItemComponentTypesSuccess: boolean;
    loadItemComponentTypesFailure: boolean;
    loadItemComponentTypesErrorCode: string;
    clearItemComponent: ItemComponentActions.IItemComponentClearActionCreator;
    loadItemComponent: ItemComponentActions.IItemComponentLoadByIdActionCreator;
    saveItemComponent: ItemComponentActions.IItemComponentSaveActionCreator;
    createItemComponent: ItemComponentActions.IItemComponentCreateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setPreviewModalViewState: ItemComponentPageActions.IItemComponentPageSetPreviewModalViewStateActionCreator;
    setAddPrimitiveModalViewState: ItemComponentPageActions.IItemComponentPageSetAddPrimitiveModalViewStateActionCreator;
    setRulesModalViewState: ItemComponentPageActions.IItemComponentPageSetRulesModalViewStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeClearActionCreator;
    loadItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeLoadActionCreator;
}

const reduxFormName: string = "reduxForms.itemComponent";
const reduxFormNameItemComponentPrimitives: string = "reduxForms.itemComponent.itemComponentPrimitives";

const ItemComponentPage: FC<ItemComponentPageProps> = ({
    form,
    formState,
    formProperties,
    previewModalOpen,
    addPrimitiveModalOpen,
    rulesModalOpen,
    itemComponent,
    lookups,
    loadingItemComponent,
    loadItemComponentSuccess,
    loadItemComponentFailure,
    loadItemComponentErrorCode,
    savingItemComponent,
    saveItemComponentSuccess,
    saveItemComponentFailure,
    creatingItemComponent,
    createItemComponentSuccess,
    // createItemComponentFailure,
    validationFailures,
    permissions,
    itemComponentTypes,
    loadingItemComponentTypes,
    loadItemComponentTypesSuccess,
    loadItemComponentTypesFailure,
    loadItemComponentTypesErrorCode,
    clearItemComponent,
    loadItemComponent,
    saveItemComponent,
    createItemComponent,
    loadForm,
    changeForm,
    resetForm,
    setPreviewModalViewState,
    setAddPrimitiveModalViewState,
    setRulesModalViewState,
    setBreadcrumbs,
    setDrawerItems,
    clearItemComponentTypes,
    loadItemComponentTypes
}) => {
    //
    // -- Params --
    const { itemComponentId } = useParams<{ itemComponentId: string }>();
    const itemComponentIdNumber = parseInt(itemComponentId, 10);
    const { cloneId } = useParams<{ cloneId: string }>();
    const cloneIdNumber = parseInt(cloneId, 10);

    //
    // -- States --
    const [rootFolder, setRootFolder] = useState<string>("/settings/trial/itemcomponents");
    const [qaItemComponents, setQaItemComponents] = useState<boolean>(false);
    const [itemComponentTypeId, setItemComponentTypeId] = useState<number>(0);
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();
    const location = useLocation();

    //
    // -- Variables --
    const _showItemComponents =
        !loadingItemComponent &&
        loadItemComponentSuccess &&
        !loadingItemComponentTypes &&
        loadItemComponentTypesSuccess;

    const loadReduxForm = () => {
        let extraData: Dtos.ItemComponent = {} as Dtos.ItemComponent;

        loadForm(reduxFormName, Object.assign({ ...itemComponent }, extraData));
    };


    // Load item component
    useEffect(() => {
        if (!itemComponentTypes && !loadingItemComponentTypes) {
            loadItemComponentTypes();
        }

        let qaItemComponentsTemp: boolean = location.pathname.indexOf("/qa/") > -1;
        setQaItemComponents(qaItemComponentsTemp);

        let rootFolderTemp: string =
            location.pathname.indexOf("/group/") > -1
                ? "/settings/group/itemcomponents"
                : location.pathname.indexOf("/qa/") > -1
                ? "/settings/qa/formcomponents"
                : location.pathname.indexOf("/project/") > -1
                ? "/settings/project/itemcomponents"
                : location.pathname.indexOf("/institution/") > -1
                ? "/settings/institution/itemcomponents"
                : location.pathname.indexOf("/trialinstitution/") > -1
                ? "/settings/trialinstitution/itemcomponents"
                : location.pathname.indexOf("/trialgroup/") > -1
                ? "/settings/trialgroup/itemcomponents"
                : "/settings/trial/itemcomponents";
        setRootFolder(rootFolderTemp);

        if (itemComponentTypes) {
            let itemComponentTypeIdTemp: number =
                location.pathname.indexOf("/group/") > -1
                    ? itemComponentTypes.find((x) => x.value === "Group")?.id
                    : location.pathname.indexOf("/project/") > -1
                    ? itemComponentTypes.find((x) => x.value === "Project")?.id
                    : location.pathname.indexOf("/institution/") > -1
                    ? itemComponentTypes.find((x) => x.value === "Institution")?.id
                    : location.pathname.indexOf("/trialinstitution/") > -1
                    ? itemComponentTypes.find((x) => x.value === "TrialInstitution")?.id
                    : location.pathname.indexOf("/trialgroup/") > -1
                    ? itemComponentTypes.find((x) => x.value === "TrialGroup")?.id
                    : location.pathname.indexOf("/trial/") > -1
                    ? itemComponentTypes.find((x) => x.value === "Trial")?.id
                    : 1;
            setItemComponentTypeId(itemComponentTypeIdTemp);
        }

        if (itemComponentIdNumber && !itemComponent && !loadingItemComponent) {
            loadItemComponent(itemComponentIdNumber);
        } else if (
            !itemComponentIdNumber &&
            !creatingItemComponent &&
            !createItemComponentSuccess
        ) {
            createItemComponent(cloneIdNumber ? cloneIdNumber : null);
        }

        // Set correct drawer and breadcrumb items
        if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Group")?.id) {
            // Group Item Component
            if (!itemComponentIdNumber) {
                setDrawerItems([]);
                setBreadcrumbs(GroupItemComponentCreateBreadcrumbs());
            } else {
                setDrawerItems(SettingsGroupsSubMenu(permissions));
                setBreadcrumbs(GroupItemComponentBreadcrumbs(itemComponentIdNumber));
            }
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Project")?.id) {
            // Project Item Components
            if (!itemComponentIdNumber) {
                setDrawerItems([]);
                setBreadcrumbs(ProjectItemComponentCreateBreadcrumbs());
            } else {
                setDrawerItems(SettingsProjectsSubMenu(permissions));
                setBreadcrumbs(ProjectItemComponentBreadcrumbs(itemComponentIdNumber));
            }
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Institution")?.id) {
            // Institution Item Components
            if (!itemComponentIdNumber) {
                setDrawerItems([]);
                setBreadcrumbs(InstitutionItemComponentCreateBreadcrumbs());
            } else {
                setDrawerItems(SettingsInstitutionsSubMenu(permissions));
                setBreadcrumbs(InstitutionItemComponentBreadcrumbs(itemComponentIdNumber));
            }
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialInstitution")?.id) {
            // Trial Institution Item Components
            if (!itemComponentIdNumber) {
                setDrawerItems([]);
                setBreadcrumbs(TrialInstitutionItemComponentCreateBreadcrumbs());
            } else {
                setDrawerItems(SettingsTrialInstitutionSubMenu(permissions));
                setBreadcrumbs(TrialInstitutionItemComponentBreadcrumbs(itemComponentIdNumber)
                );
            }
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialGroup")?.id) {
            // Trial Group Item Components
            if (!itemComponentIdNumber) {
                setDrawerItems([]);
                setBreadcrumbs(TrialGroupItemComponentCreateBreadcrumbs());
            } else {
                setDrawerItems(SettingsTrialGroupSubMenu(permissions));
                setBreadcrumbs(TrialGroupItemComponentBreadcrumbs(itemComponentIdNumber));
            }
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Trial")?.id) {
            // Trial Item Components
            if (!itemComponentIdNumber) {
                setDrawerItems([]);
                setBreadcrumbs(TrialItemComponentCreateBreadcrumbs());
            } else {
                setDrawerItems(SettingsTrialSubMenu(permissions));
                setBreadcrumbs(TrialItemComponentBreadcrumbs(itemComponentIdNumber));
            }
        } else if (qaItemComponents) {
            // Form Components
            if (!itemComponentIdNumber) {
                setDrawerItems([]);
                setBreadcrumbs(FormComponentCreateBreadcrumbs());
            } else {
                setDrawerItems(SettingsQASubMenu(permissions));
                setBreadcrumbs(FormComponentBreadcrumbs(itemComponentIdNumber));
            }
        }

        if (itemComponent && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        itemComponentTypes,
        loadingItemComponentTypes,
        loadItemComponentTypes,
        location,
        setQaItemComponents,
        setRootFolder,
        setItemComponentTypeId,
        itemComponentIdNumber,
        itemComponent,
        loadingItemComponent,
        loadItemComponent,
        creatingItemComponent,
        createItemComponentSuccess,
        createItemComponent,
        setDrawerItems,
        setBreadcrumbs,
        GroupItemComponentCreateBreadcrumbs,
        SettingsGroupsSubMenu,
        GroupItemComponentBreadcrumbs,
        permissions,
        ProjectItemComponentCreateBreadcrumbs,
        SettingsProjectsSubMenu,
        ProjectItemComponentBreadcrumbs,
        InstitutionItemComponentCreateBreadcrumbs,
        SettingsInstitutionsSubMenu,
        InstitutionItemComponentBreadcrumbs,
        FormComponentCreateBreadcrumbs,
        SettingsQASubMenu,
        FormComponentBreadcrumbs,
        TrialItemComponentCreateBreadcrumbs,
        SettingsTrialSubMenu,
        TrialItemComponentBreadcrumbs,
        initalised,
        itemComponent,
        loadReduxForm,
        setInitalised,
        TrialInstitutionItemComponentCreateBreadcrumbs,
        SettingsTrialInstitutionSubMenu,
        TrialInstitutionItemComponentBreadcrumbs,
        TrialGroupItemComponentCreateBreadcrumbs,
        SettingsTrialGroupSubMenu,
        TrialGroupItemComponentBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadItemComponentFailure) {
            navigate(`/error/${loadItemComponentErrorCode}`);
        }
        if (loadItemComponentTypesFailure) {
            navigate(`/error/${loadItemComponentTypesErrorCode}`);
        }
    }, [
        loadItemComponentFailure,
        loadItemComponentErrorCode,
        loadItemComponentTypesFailure,
        loadItemComponentTypesErrorCode,
        navigate,
    ]);

    // Cleanup effect on component unmount
    useEffect(() => {
        return () => {
            clearItemComponent();
            clearItemComponentTypes();
            resetForm(reduxFormName);
            resetForm(reduxFormNameItemComponentPrimitives);
        };
    }, [clearItemComponent, clearItemComponentTypes]);

    // Update breadcrumbs
    useEffect(() => {
        if (itemComponentIdNumber && itemComponent?.name) {
            if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Group")?.id) {
                // Group Item Components
                setBreadcrumbs(
                    GroupItemComponentBreadcrumbs(
                        itemComponentIdNumber,
                        itemComponent.name
                    )
                );
            } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Project")?.id) {
                // Project Item Components
                setBreadcrumbs(
                    ProjectItemComponentBreadcrumbs(
                        itemComponentIdNumber,
                        itemComponent.name
                    )
                );
            } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Institution")?.id) {
                // Institution Item Components
                setBreadcrumbs(
                    InstitutionItemComponentBreadcrumbs(
                        itemComponentIdNumber,
                        itemComponent.name
                    )
                );
            } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialInstitution")?.id) {
                // Trial Institution Item Components
                setBreadcrumbs(
                    TrialInstitutionItemComponentBreadcrumbs(
                        itemComponentIdNumber,
                        itemComponent.name
                    )
                );
            } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialGroup")?.id) {
                // Trial Group Item Components
                setBreadcrumbs(
                    TrialGroupItemComponentBreadcrumbs(
                        itemComponentIdNumber,
                        itemComponent.name
                    )
                );
            } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Trial")?.id) {
                // Trial Item Components
                setBreadcrumbs(
                    TrialItemComponentBreadcrumbs(
                        itemComponentIdNumber,
                        itemComponent.name
                    )
                );
            } else if (qaItemComponents) {
                // Form Components
                setBreadcrumbs(
                    FormComponentBreadcrumbs(
                        itemComponentIdNumber,
                        itemComponent.name
                    )
                );
            }
        }
    }, [
        itemComponentIdNumber,
        itemComponent,
        setBreadcrumbs,
        itemComponentTypes,
        GroupItemComponentBreadcrumbs,
        ProjectItemComponentBreadcrumbs,
        InstitutionItemComponentBreadcrumbs,
        FormComponentBreadcrumbs,
        TrialItemComponentBreadcrumbs,
        TrialInstitutionItemComponentBreadcrumbs,
        TrialGroupItemComponentBreadcrumbs,
    ]);

    // Reload item component after saving
    useEffect(() => {
        if (!savingItemComponent && saveItemComponentSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Item component saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(rootFolder);
            } else if (!itemComponentIdNumber) {
                navigate(`${rootFolder}/${itemComponent?.id}`);
                clearItemComponent();
            }
        }
    }, [
        savingItemComponent,
        saveItemComponentSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingItemComponent && saveItemComponentFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingItemComponent, saveItemComponentFailure]);

    const onCancel = () => {
        navigate(rootFolder);
    };

    const handleOnSubmitFailed = (_data: Dtos.ItemComponent) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.ItemComponent = {} as Dtos.ItemComponent;

            if (itemComponentTypeId) {
                changes.itemComponentTypeId = itemComponentTypeId
            }  
            if (!data.id && itemComponentIdNumber) {
                changes.id = itemComponentIdNumber;
            }

            await saveItemComponent(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const itemPrimitiveLabel = (itemPrimitive: Dtos.ItemPrimitive) => {
        switch (itemPrimitive) {
            case Dtos.ItemPrimitive.Checkbox:
                return "Checkbox";
            case Dtos.ItemPrimitive.Text:
                return "Text";
            case Dtos.ItemPrimitive.Label:
                return "Label";
            case Dtos.ItemPrimitive.Comments:
                return "Comments";
            case Dtos.ItemPrimitive.Date:
                return "Date";
            case Dtos.ItemPrimitive.Documents:
                return "Documents";
            case Dtos.ItemPrimitive.Numeric:
                return "Numeric";
            case Dtos.ItemPrimitive.Select:
                return "Select";
        }
    };

    const onClone = () => {
        navigate(`${rootFolder}/create/${itemComponentIdNumber}`);
    };

    const renderPrimitiveButton = (label, description, itemPrimitive: Dtos.ItemPrimitive) => {
        return (
            <button
                disabled={false}
                onClick={() => {
                    const newItem: Dtos.ItemComponentPrimitive = Object.assign({
                        ...initialItemComponentPrimitiveState,
                    });
                    newItem.itemPrimitive = itemPrimitive;
                    let array: any[] = [];
                    if (form.itemComponentPrimitives.length > 0) {
                        let newRank = 1;
                        form.itemComponentPrimitives.forEach((p) => {
                            if (p.rank >= newRank) {
                                newRank = p.rank;
                            }
                        });
                        newItem.rank = newRank + 1;
                        array = [...form["itemComponentPrimitives"], newItem];
                    } else {
                        newItem.rank = 1;
                        array = [newItem];
                    }

                    changeForm(reduxFormNameItemComponentPrimitives, array);

                    setAddPrimitiveModalViewState(false);
                }}
                type="submit"
                className="btn btn-block btn-outline-dark mb-2"
            >
                {label}
                <br />
                <small>{description}</small>
            </button>
        );
    };

    const renderRulesModal = useMemo(() => {
        const formDisabled: boolean = false;

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            itemComponentRules: {
                type: RegistrationComponent.RegistrationGrid,
                label: "Rules",
                disabled: formDisabled,
                formName: "ItemComponentRule",
                className: "",
                minRows: 0,
                columnStyles: [
                    {
                        width: undefined,
                        minWidth: "200px",
                    },
                    {
                        width: "23%",
                        minWidth: "150px",
                    },
                    {
                        width: "20%",
                        minWidth: "150px",
                    },
                    {
                        width: "125px",
                    },
                ],
                removeConfig: GridRemove.All,
                addRow: (gridComponent, _index, _array) => {
                    const state: Dtos.ItemComponentRule = Object.assign({
                        ...gridComponent.initialState,
                    });
                    return state;
                },
                components: {
                    itemPrimitiveRule: {
                        type: RegistrationComponent.RegistrationControl,
                        inputType: SelectFormInput,
                        inputLabel: getFormPropertyLabel("ItemComponent", "ItemPrimitiveRule", formProperties),
                        inputProps: {
                            options: generateOptionsFromLookup(
                                findLookup("ItemComponentRule", "ItemPrimitiveRule", lookups)
                            ),
                            feedbackIcon: FontAwesomeIcons.Solid.BAN,
                        },
                        validators: {
                            required: (value) => value != undefined,
                        },
                        errors: {
                            required: "Rule type is required.",
                        },
                        errorComponent: ValidationMessage,
                    },
                    compareValue: {
                        type: RegistrationComponent.RegistrationControl,
                        inputType: TextFormInput,
                        inputDisabled: formDisabled,
                        inputLabel: getFormPropertyLabel("ItemComponent", "CompareValue", formProperties),
                    },
                    active: {
                        type: RegistrationComponent.RegistrationControl,
                        inputType: CheckboxFormInput,
                        inputDisabled: formDisabled,
                    },
                },
                initialState: initialItemComponentRuleState,
                onRemove: (model: string) => {
                    changeForm(model + "active", false);
                    return true; // this was handled here
                },
                filterRow: (data: any) => {
                    return data.active;
                },
            },
        };

        return (
            <Modal
                open={rulesModalOpen}
                disableCloseOnOverlayClick={true}
                onClose={() => {
                    setRulesModalViewState(false);
                }}
                size={ModalSize.Xl}
            >
                <div className="modal-header">
                    <h5 className="modal-title">
                        {setModalTitle(undefined, "Rules")}
                    </h5>
                    <button
                        type="button"
                        className="close"
                        aria-label="Close"
                        onClick={() => {
                            setRulesModalViewState(false);
                        }}
                    >
                        <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                    </button>
                </div>
                <div className="modal-body">
                    <div className="container-fluid">
                        {renderFormControls(
                            form,
                            "itemComponent",
                            formComponents,
                            lookups,
                            validationFailures,
                            formProperties,
                            changeForm
                        )}
                    </div>
                </div>
                <div className="modal-footer">
                    <button type="submit" className="btn btn-success">
                        Confirm
                    </button>
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => {
                            setRulesModalViewState(false);
                        }}
                    >
                        Close
                    </button>
                </div>
            </Modal>
        );
    }, [
        generateOptionsFromLookup,
        findLookup,
        lookups,
        getFormPropertyLabel,
        initialItemComponentRuleState,
        rulesModalOpen,
        setRulesModalViewState,
        setModalTitle,
        renderFormControls,
        form,
        validationFailures,
        formProperties,
        changeForm,
    ]);

    const renderAddPrimitiveModal = useMemo(() => {
        return (
            <Modal
                open={addPrimitiveModalOpen}
                disableCloseOnOverlayClick={true}
                onClose={() => {
                    setAddPrimitiveModalViewState(false);
                }}
                size={ModalSize.Md}
            >
                <div className="modal-header">
                    <h5 className="modal-title">
                        {setModalTitle(undefined, "Select primitive to add")}
                    </h5>
                    <button
                        type="button"
                        className="close"
                        aria-label="Close"
                        onClick={() => {
                            setAddPrimitiveModalViewState(false);
                        }}
                    >
                        <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                    </button>
                </div>
                <div className="modal-body">
                    {renderPrimitiveButton(
                        "Label",
                        "Display a caption that the user cannot overwrite",
                        Dtos.ItemPrimitive.Label
                    )}
                    {renderPrimitiveButton(
                        "Text",
                        "Control for the user to enter single line of free text",
                        Dtos.ItemPrimitive.Text
                    )}
                    {renderPrimitiveButton(
                        "Comments",
                        "Control for the user to enter multiple lines of free text",
                        Dtos.ItemPrimitive.Comments
                    )}
                    {renderPrimitiveButton(
                        "Check",
                        "Tick box for yes/no answers",
                        Dtos.ItemPrimitive.Checkbox
                    )}
                    {renderPrimitiveButton(
                        "Documents",
                        "Upload documents to this component",
                        Dtos.ItemPrimitive.Documents
                    )}
                    {renderPrimitiveButton(
                        "Numeric",
                        "Control to enter numbers",
                        Dtos.ItemPrimitive.Numeric
                    )}
                    {renderPrimitiveButton(
                        "Select",
                        "Choose a single item from a drop down list",
                        Dtos.ItemPrimitive.Select
                    )}
                    {renderPrimitiveButton(
                        "Date",
                        "Control to select a date",
                        Dtos.ItemPrimitive.Date
                    )}
                </div>
                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => {
                            setAddPrimitiveModalViewState(false);
                        }}
                    >
                        Close
                    </button>
                </div>
            </Modal>
        );
    }, [
        addPrimitiveModalOpen,
        setAddPrimitiveModalViewState,
        setModalTitle,
        renderPrimitiveButton,
    ]);

    const renderPreviewModal = useMemo(() => {
        const _formDisabled: boolean = false;

        let formComponents: { [index: string]: RegistrationFormComponent } = {};

        if (form && form.itemComponentPrimitives) {
            [...form.itemComponentPrimitives]
                .sort((a, b) => a.rank - b.rank)
                .filter((f) => f.active)
                .sort((a, b) => a.rank - b.rank)
                .forEach((item, index) => {
                    const name = `itemComponentInputPrimitives_${item.id}_${index}`;
                    formComponents[name] = {
                        type: RegistrationComponent.RegistrationElement,
                        component: (
                            <div className="border-0 d-flex pt-4 ">
                                <PrimitiveFormInput
                                    item={item}
                                    id={name}
                                    name={name}
                                />
                            </div>
                        ),
                    };
                });
        }

        if (form && form.customInputControlName && form.customInputControlName != "") {
            const name = `itemComponent_${form.id}_${form.customInputControlName}`;
            formComponents[name] = {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <div className="border-0 d-flex pt-4 ">
                        <CustomFormInput item={form} id={name} name={name} />
                    </div>
                ),
            };
        }

        return (
            <Modal
                open={previewModalOpen}
                disableCloseOnOverlayClick={true}
                onClose={() => {
                    setPreviewModalViewState(false);
                }}
                size={ModalSize.Md}
            >
                <div className="modal-header">
                    <h5 className="modal-title">
                        {setModalTitle(undefined, "Preview Item Component")}
                    </h5>
                    <button
                        type="button"
                        className="close"
                        aria-label="Close"
                        onClick={() => {
                            setPreviewModalViewState(false);
                        }}
                    >
                        <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                    </button>
                </div>
                <div className="modal-body">
                    <div className="container-fluid">
                        {renderFormControls(
                            form,
                            "itemComponent",
                            formComponents,
                            lookups,
                            validationFailures,
                            formProperties,
                            changeForm
                        )}
                    </div>
                </div>
                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => {
                            setPreviewModalViewState(false);
                        }}
                    >
                        Close
                    </button>
                </div>
            </Modal>
        );
    }, [
        form,
        previewModalOpen,
        setPreviewModalViewState,
        setModalTitle,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
    ]);

    const renderContent = useMemo(() => {
        let canUpdate: boolean = false;
        if (qaItemComponents) {
            canUpdate = AuthenticationHelper.hasPermission(
                permissions,
                Dtos.Permission.FormComponentsUpdate
            );
        } else {
            if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Group")?.id) {
                canUpdate = AuthenticationHelper.hasPermission(
                    permissions,
                    Dtos.Permission.GroupItemComponentsUpdate
                );
            } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Project")?.id) {
                canUpdate = AuthenticationHelper.hasPermission(
                    permissions,
                    Dtos.Permission.ProjectItemComponentsUpdate
                );
            } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Institution")?.id) {
                canUpdate = AuthenticationHelper.hasPermission(
                    permissions,
                    Dtos.Permission.InstitutionItemComponentsUpdate
                );
            } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialInstitution")?.id) {
                canUpdate = AuthenticationHelper.hasPermission(
                    permissions,
                    Dtos.Permission.TrialInstitutionItemComponentsUpdate
                );
            } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialGroup")?.id) {
                canUpdate = AuthenticationHelper.hasPermission(
                    permissions,
                    Dtos.Permission.TrialGroupItemComponentsUpdate
                );
            } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Trial")?.id) {
                canUpdate = AuthenticationHelper.hasPermission(
                    permissions,
                    Dtos.Permission.ItemComponentsUpdate
                );
            }

        }

        const formDisabled: boolean =
            !canUpdate ||
            savingItemComponent ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled) ||
            form.isSystem;

        let itemComponentPrimitivesLookup: Dtos.Lookup = {
            type: "ItemComponentRule",
            propertyName: "ItemComponentPrimitive",
            values: [],
            groupedValues: [],
        };
        if (
            itemComponent &&
            itemComponent.itemComponentPrimitives &&
            itemComponent.itemComponentPrimitives.length > 0
        ) {
            itemComponentPrimitivesLookup.values =
                itemComponent.itemComponentPrimitives
                    .filter(
                        (f) =>
                            [
                                Dtos.ItemPrimitive.Comments,
                                Dtos.ItemPrimitive.Text,
                                Dtos.ItemPrimitive.Date,
                                Dtos.ItemPrimitive.Select,
                                Dtos.ItemPrimitive.Numeric,
                            ].indexOf(f.itemPrimitive) > -1 && f.active
                    )
                    .map((item, _index) => {
                        return {
                            id: item.id,
                            value: `${itemPrimitiveLabel(item.itemPrimitive)} [${item.label}]`,
                            rank: item.rank,
                            active: true,
                            group: "",
                        };
                    });
        }
        let compareItemComponentPrimitivesLookup: Dtos.Lookup = {
            type: "ItemComponentRule",
            propertyName: "CompareItemComponentPrimitive",
            values: [],
            groupedValues: [],
        };
        if (
            itemComponent &&
            itemComponent.itemComponentPrimitives &&
            itemComponent.itemComponentPrimitives.length > 0
        ) {
            compareItemComponentPrimitivesLookup.values =
                itemComponent.itemComponentPrimitives
                    .filter(
                        (f) =>
                            [
                                Dtos.ItemPrimitive.Comments,
                                Dtos.ItemPrimitive.Text,
                                Dtos.ItemPrimitive.Select,
                                Dtos.ItemPrimitive.Numeric,
                                Dtos.ItemPrimitive.Checkbox,
                            ].indexOf(f.itemPrimitive) > -1 && f.active
                    )
                    .map((item, _index) => {
                        return {
                            id: item.id,
                            value: `${itemPrimitiveLabel(item.itemPrimitive)} [${item.label}]`,
                            rank: item.rank,
                            active: true,
                            group: "",
                        };
                    });
        }

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "itemComponent",
                                        {
                                            name: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2",
                                                fieldClassName: "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "itemComponent",
                                        {
                                            description: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextAreaFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: { rows: 3 },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2",
                                                fieldClassName: "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "itemComponent",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-2 d-none",
                                                fieldClassName: "offset-sm-2 col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            documentsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Documents and Downloads</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "itemComponent",
                                        {
                                            sourceDocuments: {
                                                type: RegistrationComponent.RegistrationSourceDocuments,
                                                label: " ",
                                                disabled: formDisabled,
                                                components: {
                                                    sourceDocuments: {
                                                        type: RegistrationComponent.RegistrationGrid,
                                                        formName: "SourceDocument",
                                                        disabled: formDisabled,
                                                        label: "Add Source Document",
                                                        className: "source-documents-source-documents",
                                                        minRows: 0,
                                                        columnStyles: [
                                                            {
                                                                width: undefined,
                                                                minWidth:
                                                                    "200px",
                                                            },
                                                            {
                                                                width: "23%",
                                                                minWidth:
                                                                    "150px",
                                                            },
                                                            {
                                                                width: "20%",
                                                                minWidth:
                                                                    "150px",
                                                            },
                                                            {
                                                                width: "125px",
                                                            },
                                                        ],
                                                        removeConfig: GridRemove.All,
                                                        addRow: (gridComponent, _index, _array) => {
                                                            const state: Dtos.SourceDocument =
                                                                Object.assign({
                                                                    ...gridComponent.initialState,
                                                                });
                                                            return state;
                                                        },
                                                        components: {
                                                            name: {
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: TextFormInput,
                                                                inputDisabled: formDisabled,
                                                                inputLabel: getFormPropertyLabel("CollectionInstance", "FileName", formProperties),
                                                                inputProps: {
                                                                    feedbackIcon: FontAwesomeIcons.Solid.BAN,
                                                                },
                                                                validators: {
                                                                    required: requiredText,
                                                                },
                                                                errors: {
                                                                    required: "File name is required.",
                                                                },
                                                                errorComponent: ValidationMessage,
                                                            },
                                                            fileType: {
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: SelectFormInput,
                                                                inputLabel: getFormPropertyLabel("CollectionInstance", "FileType", formProperties),
                                                                inputProps: {
                                                                    options:
                                                                        generateOptionsFromLookup(
                                                                            findLookup("SourceDocument", "FileType", lookups)
                                                                        ),
                                                                    feedbackIcon: FontAwesomeIcons.Solid.BAN,
                                                                },
                                                                validators: {
                                                                    required: (value) => value != undefined,
                                                                },
                                                                errors: {
                                                                    required: "File type is required.",
                                                                },
                                                                errorComponent: ValidationMessage,
                                                            },
                                                            fileInformation: {
                                                                type: RegistrationComponent.RegistrationControl,
                                                                inputType: FileUploadFormInput,
                                                                inputDisabled: formDisabled,
                                                                inputLabel: getFormPropertyLabel("CollectionInstance", "Upload", formProperties),
                                                                changeAction: (model, value, form, parentModel) => {
                                                                    changeForm(model, value);
                                                                    const inputValue: Dtos.TemporaryFileInformation = value;
                                                                    const sourceDocument: Dtos.SourceDocument = form;

                                                                    if (
                                                                        sourceDocument.fileInformation &&
                                                                        inputValue &&
                                                                        sourceDocument.fileInformation.state != inputValue.state &&
                                                                        inputValue.state == Dtos.FileState.Success &&
                                                                        !sourceDocument.name
                                                                    ) {
                                                                        changeForm(
                                                                            reduxFormName + parentModel + ".name",
                                                                            inputValue.temporaryFile.name.substr(
                                                                                0,
                                                                                inputValue.temporaryFile.name.lastIndexOf(
                                                                                    inputValue.temporaryFile.extension
                                                                                )
                                                                            )
                                                                        );
                                                                    }
                                                                },
                                                                inputProps: {
                                                                    render: SOURCE_DOCUMENT_UPLOAD_VIEW,
                                                                },
                                                                validators: {
                                                                    required:
                                                                        requiredSourceDocument,
                                                                },
                                                                errors: {
                                                                    required:
                                                                        "File must be uploaded.",
                                                                },
                                                                errorComponent:
                                                                    ValidationMessage,
                                                            },
                                                            fileConfiguration: {
                                                                type: RegistrationComponent.RegistrationGridElement,
                                                                label: " ",
                                                                render: SOURCE_DOCUMENT_FILE_CONFIGURATION,
                                                            },
                                                        },
                                                        initialState: {
                                                            ...initialSourceDocumentState,
                                                        },
                                                    },
                                                },
                                                keyReplace: "sourceDocuments",
                                                onRemove: (model: string) => {
                                                    changeForm(model + "active", false);
                                                    return true; // this was handled here
                                                },
                                                filterRow: (data: Dtos.SourceDocument) => {
                                                    return data.active;
                                                },
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            rulesGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Rules</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-4 pb-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "itemComponent",
                                        {
                                            itemComponentRules: {
                                                type: RegistrationComponent.RegistrationGrid,
                                                label: "Rules",
                                                disabled: formDisabled,
                                                formName: "ItemComponentRule",
                                                className: "",
                                                minRows: 0,
                                                columnStyles: [
                                                    {
                                                        width: undefined,
                                                        minWidth: "250px",
                                                    },
                                                    {
                                                        width: undefined,
                                                        minWidth: "200px",
                                                    },
                                                    {
                                                        width: undefined,
                                                        minWidth: "250px",
                                                    },
                                                    {
                                                        width: undefined,
                                                        minWidth: "130px",
                                                    },
                                                    {
                                                        width: undefined,
                                                        minWidth: "100px",
                                                    },
                                                    {
                                                        width: "125px",
                                                    },
                                                ],
                                                removeConfig: GridRemove.All,
                                                addRow: (gridComponent, _index, _array) => {
                                                    const state: Dtos.ItemComponentRule =
                                                        Object.assign({
                                                            ...gridComponent.initialState,
                                                        });
                                                    return state;
                                                },
                                                components: {
                                                    itemComponentPrimitiveId: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: SelectFormInput,
                                                        inputLabel: getFormPropertyLabel("ItemComponentRule", "ItemComponentPrimitiveId", formProperties),
                                                        inputProps: {
                                                            options: generateOptionsFromLookup(itemComponentPrimitivesLookup),
                                                            feedbackIcon: FontAwesomeIcons.Solid.BAN,
                                                        },
                                                        validators: {
                                                            required: (value) => value != undefined,
                                                        },
                                                        errors: {
                                                            required: "Item Component is required.",
                                                        },
                                                        errorComponent: ValidationMessage,
                                                    },
                                                    itemPrimitiveRule: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: SelectFormInput,
                                                        inputLabel: getFormPropertyLabel("ItemComponentRule", "ItemPrimitiveRule", formProperties),
                                                        inputProps: {
                                                            options:
                                                                generateOptionsFromLookup(
                                                                    findLookup("ItemComponentRule", "ItemPrimitiveRule", lookups)
                                                                ),
                                                            feedbackIcon: FontAwesomeIcons.Solid.BAN,
                                                        },
                                                        validators: {
                                                            required: (value) => value != undefined,
                                                        },
                                                        errors: {
                                                            required: "Rule type is required.",
                                                        },
                                                        errorComponent: ValidationMessage,
                                                        changeAction: (
                                                            model,
                                                            value,
                                                            _form,
                                                            parentModel
                                                        ) => {
                                                            changeForm(model, value);
                                                            if (value == Dtos.ItemPrimitiveRule.Required) {
                                                                changeForm(reduxFormName + parentModel + ".compareItemComponentPrimitiveId", null);
                                                                changeForm(reduxFormName + parentModel + ".compareValue", null);
                                                            }
                                                        },
                                                    },
                                                    compareItemComponentPrimitiveId:
                                                        {
                                                            type: RegistrationComponent.RegistrationControl,
                                                            inputType: SelectFormInput,
                                                            inputLabel: getFormPropertyLabel("ItemComponentRule", "CompareItemComponentPrimitiveId", formProperties),
                                                            inputProps: {
                                                                options: generateOptionsFromLookup(compareItemComponentPrimitivesLookup),
                                                                feedbackIcon: FontAwesomeIcons.Solid.BAN,
                                                            },
                                                            disabledFn: (form: Dtos.ItemComponentRule, _arrayKey: string) => {
                                                                return form?.itemPrimitiveRule == Dtos.ItemPrimitiveRule.Required;
                                                            },
                                                        },
                                                    compareValue: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: TextFormInput,
                                                        inputDisabled: formDisabled,
                                                        inputLabel: getFormPropertyLabel("ItemComponentRule", "CompareValue", formProperties),
                                                        disabledFn: (form: Dtos.ItemComponentRule, _arrayKey: string) => {
                                                            return form?.itemPrimitiveRule == Dtos.ItemPrimitiveRule.Required;
                                                        },
                                                    },
                                                    active: {
                                                        type: RegistrationComponent.RegistrationControl,
                                                        inputType: CheckboxFormInput,
                                                        inputDisabled: formDisabled,
                                                        inputLabel: getFormPropertyLabel("ItemComponentRule", "Active", formProperties),
                                                    },
                                                },
                                                initialState: initialItemComponentRuleState,
                                                onRemove: (model: string) => {
                                                    changeForm(model + "active", false);
                                                    return true; // this was handled here
                                                },
                                                filterRow: (data: any) => {
                                                    return data.active;
                                                },
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            primitivesHeader: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Component Primitives</h1>
                    </>
                ),
            },
        };

        if (
            form.itemComponentPrimitives &&
            form.itemComponentPrimitives.filter((f) => f.active).length == 0
        ) {
            const name = "itemComponentPrimitives_empty";
            formComponents[name] = {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <div className="card card-shadow p-3">
                        <div className="alert alert-info d-flex mb-0">
                            <div className="mr-2">
                                <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                            </div>
                            <span>
                                Add one or more primitives to start building the item component.
                            </span>
                        </div>
                    </div>
                ),
            };
        } else if (
            form.itemComponentPrimitives &&
            form.itemComponentPrimitives.length > 0
        ) {
            [...form.itemComponentPrimitives]
                .sort((a, b) => a.rank - b.rank)
                .forEach((item, index) => {
                    const name = `itemComponentPrimitives_${item.id}_${index}_${item.rank}`;
                    formComponents[name] = {
                        type: RegistrationComponent.RegistrationElement,
                        component: !item.active ? (
                            <div></div>
                        ) : (
                            <>
                                <div className="card card-shadow p-3">
                                    <div className="border-0 d-flex">
                                        <PrimitiveFormDesigner
                                            index={index + 1}
                                            item={item}
                                            id={name}
                                            name={name}
                                            disabled={formDisabled}
                                            // onShowRules={(item) => { setRulesModalViewState(true); }}
                                            changeAction={(name, value) => {
                                                changeForm(`${reduxFormNameItemComponentPrimitives}[${index.toString()}].${name}`, value);
                                            }}
                                            onUp={(item) => {
                                                // find the previous rank, and then swap
                                                let swapIndex = -1;
                                                let swapRank = -1;
                                                let newArray = [...form.itemComponentPrimitives].sort((a, b) => a.rank - b.rank);
                                                newArray.forEach((swapItem, i) => {
                                                        if (i < index && swapItem.active) {
                                                            swapIndex = i;
                                                            swapRank = swapItem.rank;
                                                        }
                                                    }
                                                );
                                                if (swapIndex > -1) {
                                                    newArray[swapIndex] =
                                                        Object.assign({
                                                            ...newArray[swapIndex],
                                                            rank: item.rank,
                                                        });
                                                    newArray[index] =
                                                        Object.assign({
                                                            ...newArray[index],
                                                            rank: swapRank,
                                                        });
                                                    changeForm(reduxFormNameItemComponentPrimitives, newArray);
                                                }
                                            }}
                                            onDown={() => {
                                                // find the previous rank, and then swap
                                                let swapIndex = -1;
                                                let swapRank = -1;
                                                let newArray = [...form.itemComponentPrimitives].sort((a, b) => a.rank - b.rank);
                                                newArray.forEach(
                                                    (swapItem, i) => {
                                                        if (i > index && swapItem.active && swapIndex == -1) {
                                                            swapIndex = i;
                                                            swapRank = swapItem.rank;
                                                        }
                                                    }
                                                );
                                                if (swapIndex > -1) {
                                                    newArray[swapIndex] =
                                                        Object.assign({
                                                            ...newArray[swapIndex],
                                                            rank: item.rank,
                                                        });
                                                    newArray[index] =
                                                        Object.assign({
                                                            ...newArray[index],
                                                            rank: swapRank,
                                                        });
                                                    changeForm(reduxFormNameItemComponentPrimitives, newArray);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </>
                        ),
                    };
                });
        }
        if (form.customInputControlName && form.customInputControlName != "") {
            const name = `itemComponent_${form.id}_${form.customInputControlName}`;
            formComponents[name] = {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <div className="card card-shadow p-3">
                        <div className="border-0 d-flex">
                            <CustomFormDesigner
                                item={form}
                                id={name}
                                name={name}
                            />
                        </div>
                    </div>
                ),
            };
        }

        let label = "";
        if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Group")?.id) {
            label = "Group Item Components";
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Project")?.id) {
            label = "Project Item Component";
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Institution")?.id) {
            label = "Institution Item Component";
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialInstitution")?.id) {
            label = "Trial Institution Item Component";
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialGroup")?.id) {
            label = "Trial Group Item Component";
        } else if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Trial")?.id) {
            label = "Trial Item Component";
        } else if (qaItemComponents) {
            label = "Form Component";
        }

        return (
            <div>
                <h1>{label}</h1>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "itemComponent",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                setAddPrimitiveModalViewState(true);
                            }}
                            type="button"
                            className="btn btn-outline-secondary mr-2"
                        >
                            Add primitive
                        </button>
                        <button
                            disabled={false}
                            onClick={() => {
                                setPreviewModalViewState(true);
                            }}
                            type="button"
                            className="btn btn-secondary mr-2"
                        >
                            Preview
                        </button>
                        <button
                            disabled={
                                loadingItemComponent ||
                                savingItemComponent ||
                                !itemComponentIdNumber
                            }
                            onClick={onClone}
                            className="btn btn-secondary mr-2"
                        >
                            Clone
                        </button>
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", false);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(reduxFormName + ".saveAndReturn", true);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        qaItemComponents,
        permissions,
        itemComponentTypeId,
        itemComponentTypes,
        savingItemComponent,
        formState,
        itemComponent,
        itemPrimitiveLabel,
        form,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        generateOptionsFromLookup,
        findLookup,
        getFormPropertyLabel,
        changeForm,
        reduxFormName,
        reduxFormNameItemComponentPrimitives,
        handleOnSubmit,
        handleOnSubmitFailed,
        setAddPrimitiveModalViewState,
        setPreviewModalViewState,
        loadingItemComponent,
        savingItemComponent,
        itemComponentIdNumber,
    ]);

    const render = useMemo(() => {
        if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Group")?.id) {
            return (
                <GroupSettingsLayout
                    loading={loadingItemComponent}
                    permission={Dtos.Permission.GroupItemComponentsView}
                >
                    {renderContent}
                    {renderPreviewModal}
                    {renderAddPrimitiveModal}
                    {renderRulesModal}
                </GroupSettingsLayout>
            );
        }

        if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Project")?.id) {
            return (
                <ProjectLookupsLayout
                    loading={loadingItemComponent}
                    permission={Dtos.Permission.ProjectItemComponentsView}
                >
                    {renderContent}
                    {renderPreviewModal}
                    {renderAddPrimitiveModal}
                    {renderRulesModal}
                </ProjectLookupsLayout>
            );
        }

        if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "Institution")?.id) {
            return (
                <InstitutionLookupsLayout
                    loading={loadingItemComponent}
                    permission={Dtos.Permission.InstitutionItemComponentsView}
                >
                    {renderContent}
                    {renderPreviewModal}
                    {renderAddPrimitiveModal}
                    {renderRulesModal}
                </InstitutionLookupsLayout>
            );
        }

        if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialInstitution")?.id) {
            return (
                <TrialInstitutionLookupsLayout
                    loading={loadingItemComponent}
                    permission={
                        Dtos.Permission.TrialInstitutionItemComponentsView
                    }
                >
                    {renderContent}
                    {renderPreviewModal}
                    {renderAddPrimitiveModal}
                    {renderRulesModal}
                </TrialInstitutionLookupsLayout>
            );
        }

        if (itemComponentTypeId == itemComponentTypes?.find((x) => x.value === "TrialGroup")?.id) {
            return (
                <TrialGroupLookupsLayout
                    loading={loadingItemComponent}
                    permission={Dtos.Permission.TrialGroupItemComponentsView}
                >
                    {renderContent}
                    {renderPreviewModal}
                    {renderAddPrimitiveModal}
                    {renderRulesModal}
                </TrialGroupLookupsLayout>
            );
        }

        if (qaItemComponents) {
            return (
                <QALookupsLayout
                    loading={loadingItemComponent}
                    permission={Dtos.Permission.FormComponentsView}
                >
                    {renderContent}
                    {renderPreviewModal}
                    {renderAddPrimitiveModal}
                    {renderRulesModal}
                </QALookupsLayout>
            );
        }

        return (
            <TrialLookupsLayout
                permission={Dtos.Permission.ItemComponentsView}
                loading={loadingItemComponent}
            >
                {renderContent}
                {renderPreviewModal}
                {renderAddPrimitiveModal}
                {renderRulesModal}
            </TrialLookupsLayout>
        );
    }, [
        itemComponentTypeId,
        itemComponentTypes,
        loadingItemComponent,
        renderContent,
        renderPreviewModal,
        renderAddPrimitiveModal,
        renderRulesModal,
    ]);

    return <>{render}</>;
};

const mapStateToProps = (state: ITmdState): Partial<ItemComponentPageProps> => {
    let itemComponent: Dtos.ItemComponent | undefined = !(
        state.itemComponent.formData instanceof Array
    )
        ? state.itemComponent.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];
    let itemComponentTypes =
        state.itemComponentTypes.formData instanceof Array
            ? state.itemComponentTypes.formData
            : undefined;
    return {
        itemComponent: itemComponent!,
        itemComponentTypes: itemComponentTypes!,

        loadingItemComponent: state.itemComponent.loadState && state.itemComponent.loadState.status == RequestState.Pending,
        loadItemComponentSuccess: state.itemComponent.loadState && state.itemComponent.loadState.status == RequestState.Success,
        loadItemComponentFailure: state.itemComponent.loadState && state.itemComponent.loadState.status == RequestState.Failure,
        loadItemComponentErrorCode: state.itemComponent.loadState.errorCode,

        savingItemComponent: state.itemComponent.saveState && state.itemComponent.saveState.status == RequestState.Pending,
        saveItemComponentSuccess: state.itemComponent.saveState && state.itemComponent.saveState.status == RequestState.Success,
        saveItemComponentFailure: state.itemComponent.saveState && state.itemComponent.saveState.status == RequestState.Failure,

        creatingItemComponent: state.itemComponent.createState && state.itemComponent.createState.status == RequestState.Pending,
        createItemComponentSuccess: state.itemComponent.createState && state.itemComponent.createState.status == RequestState.Success,
        createItemComponentFailure: state.itemComponent.createState && state.itemComponent.createState.status == RequestState.Failure,

        loadingItemComponentTypes: state.itemComponentTypes.loadState && state.itemComponentTypes.loadState.status == RequestState.Pending,
        loadItemComponentTypesSuccess: state.itemComponentTypes.loadState && state.itemComponentTypes.loadState.status == RequestState.Success,
        loadItemComponentTypesFailure: state.itemComponentTypes.loadState && state.itemComponentTypes.loadState.status == RequestState.Failure,
        loadItemComponentTypesErrorCode: state.itemComponentTypes.loadState.errorCode,

        permissions: permissions,
        lookups: state.itemComponent.lookups,

        form: state.reduxForms.itemComponent,
        formState: state.itemComponent.formState,
        formProperties: state.itemComponent.formProperties,
        validationFailures: itemComponent
            ? state.itemComponent.validationFailures
            : undefined,

        previewModalOpen: state.itemComponentPage.previewModalOpen,
        addPrimitiveModalOpen: state.itemComponentPage.addPrimitiveModalOpen,
        rulesModalOpen: state.itemComponentPage.rulesModalOpen,
    };
};

const mapDispatchToProps = (dispatch): Partial<ItemComponentPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadItemComponent: bindActionCreators(ItemComponentActions.LoadItemComponentById, dispatch),
        clearItemComponent: bindActionCreators(ItemComponentActions.Clear, dispatch),
        saveItemComponent: bindActionCreators(ItemComponentActions.SaveItemComponent, dispatch),
        createItemComponent: bindActionCreators(ItemComponentActions.CreateItemComponent, dispatch),
        setPreviewModalViewState: bindActionCreators(ItemComponentPageActions.setPreviewModalViewState, dispatch),
        setAddPrimitiveModalViewState: bindActionCreators(ItemComponentPageActions.setAddPrimitiveModalViewState, dispatch),
        setRulesModalViewState: bindActionCreators(ItemComponentPageActions.setRulesModalViewState, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        loadItemComponentTypes: bindActionCreators(ItemComponentTypeActions.LoadItemComponentType, dispatch),
        clearItemComponentTypes: bindActionCreators(ItemComponentTypeActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ItemComponentPage);
